/**
 * Dashboard REST API Client — typed fetch wrapper for all CNS endpoints.
 * @module lib/api/api-client
 *
 * Covers 13 route modules (40+ endpoints):
 *   visits, jobs, consent, llm, smartfill,
 *   autopilot, documents, quota, prompts, bindings,
 *   workflows, events, ws
 *
 * Pattern:
 * - All methods return `Promise<T>` and throw `ApiError` on failure
 * - Base URL comes from dashboard env (`VITE_API_URL`)
 * - Auth token (Supabase JWT) will be attached when auth is wired
 */

import { getDashboardEnv } from '@/lib/env';
import type { OcrJob } from '@assistmd/types';

// ─────────────────────────────────────────────────────────────────────────────
// Error Type
// ─────────────────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: unknown,
  ) {
    super(`API ${status}: ${statusText}`);
    this.name = 'ApiError';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Base Fetch
// ─────────────────────────────────────────────────────────────────────────────

let authToken: string | null = null;

/** Set the auth token for all subsequent requests. */
export function setAuthToken(token: string | null): void {
  authToken = token;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const { apiUrl } = getDashboardEnv();
  const url = `${apiUrl}${path}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const res = await fetch(url, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => res.statusText);
    throw new ApiError(res.status, res.statusText, body);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  return res.json() as Promise<T>;
}

function get<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'GET' });
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

function patch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
}

function put<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

function del<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. VISITS
// ─────────────────────────────────────────────────────────────────────────────

export const visits = {
  list: () =>
    get<unknown[]>('/api/visits'),

  create: (data: { doctorId: string; patientCode: string }) =>
    post<{ visitId: string }>('/api/visits', data),

  get: (visitId: string) =>
    get<{ visitId: string; state: unknown }>(`/api/visits/${visitId}`),

  close: (visitId: string) =>
    post<{ ok: boolean }>(`/api/visits/${visitId}/close`),

  reconstruct: (visitId: string, timestamp: number) =>
    get<{ state: unknown }>(`/api/visits/${visitId}/reconstruct?timestamp=${timestamp}`),

  getActiveForDoctor: (doctorId: string) =>
    get<{ visitId: string } | null>(`/api/visits/doctors/${doctorId}/active-visit`),
};

// ─────────────────────────────────────────────────────────────────────────────
// 2. JOBS (Automation)
// ─────────────────────────────────────────────────────────────────────────────

export interface JobCreatePayload {
  type: string;
  input: unknown;
  visitId?: string;
  priority?: number;
  category?: string;
}

export interface JobInfo {
  /** CNS AutomationJob uses `id`; dashboard historically used `jobId`. Accept both. */
  jobId: string;
  id?: string;
  type: string;
  status: string;
  progress?: number;
  progressPct?: number;
  input: unknown;
  output?: unknown;
  result?: Record<string, unknown>;
  error?: string | null;
  createdAt: number;
  updatedAt: number;
  startedAt?: number;
  completedAt?: number;
  retryCount: number;
  maxRetries: number;
  artifacts: Array<{ id: string; kind: string; url: string; label?: string; sizeBytes?: number; createdAt: number }>;
  priority?: string;
  category?: string;
  visitId?: string;
}

export const jobs = {
  create: (data: JobCreatePayload) =>
    post<{ jobId: string }>('/api/jobs', data),

  list: (params?: { status?: string; visitId?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.visitId) qs.set('visitId', params.visitId);
    const q = qs.toString();
    return get<JobInfo[]>(`/api/jobs${q ? `?${q}` : ''}`);
  },

  get: (jobId: string) =>
    get<JobInfo>(`/api/jobs/${jobId}`),

  run: (jobId: string) =>
    post<{ ok: boolean }>(`/api/jobs/${jobId}/run`),

  cancel: (jobId: string) =>
    del<{ ok: boolean }>(`/api/jobs/${jobId}`),
};

// ─────────────────────────────────────────────────────────────────────────────
// 3. CONSENT
// ─────────────────────────────────────────────────────────────────────────────

export const consent = {
  getState: (visitId: string) =>
    get<{ state: string }>(`/api/consent/${visitId}/state`),

  isConsentGranted: (visitId: string) =>
    get<{ granted: boolean }>(`/api/consent/${visitId}/granted`),

  getEventLog: (visitId: string) =>
    get<Array<{ eventType: string; timestamp: number }>>(`/api/consent/${visitId}/event-log`),

  getSession: (visitId: string) =>
    get<{ sessionId: string; state: string }>(`/api/consent/${visitId}/session`),

  processEvent: (visitId: string, data: { doctorId: string; eventType: string; source: string }) =>
    post<{ ok: boolean }>(`/api/consent/${visitId}/events`, data),
};

// ─────────────────────────────────────────────────────────────────────────────
// 4. LLM
// ─────────────────────────────────────────────────────────────────────────────

export const llm = {
  enqueueTask: (data: { input: unknown; priority?: number }) =>
    post<{ taskId: string }>('/api/llm/tasks', data),

  getTask: (taskId: string) =>
    get<{ taskId: string; status: string; result?: unknown }>(`/api/llm/tasks/${taskId}`),

  cancelTask: (taskId: string) =>
    del<{ ok: boolean }>(`/api/llm/tasks/${taskId}`),

  getMetrics: () =>
    get<{ queued: number; running: number; completed: number; failed: number }>('/api/llm/metrics'),
};

// ─────────────────────────────────────────────────────────────────────────────
// 5. SMARTFILL
// ─────────────────────────────────────────────────────────────────────────────

export const smartfill = {
  generatePlan: (data: { visitId: string; transcript: string; domSurfaces: unknown[]; options?: unknown }) =>
    post<{ planId: string; stepCount: number }>('/api/smartfill/plans', data),

  getPlan: (planId: string) =>
    get<{ planId: string; steps: unknown[] }>(`/api/smartfill/plans/${planId}`),

  validatePlan: (planId: string) =>
    post<{ valid: boolean; errors?: string[] }>(`/api/smartfill/plans/${planId}/validate`),

  invalidatePlan: (planId: string) =>
    del<{ ok: boolean }>(`/api/smartfill/plans/${planId}`),

  classify: (surfaces: unknown[]) =>
    post<{ classifications: unknown[] }>('/api/smartfill/classify', { surfaces }),
};

// ─────────────────────────────────────────────────────────────────────────────
// 6. AUTOPILOT
// ─────────────────────────────────────────────────────────────────────────────

export const autopilot = {
  enable: (visitId: string, data: { mode: string; config?: unknown }) =>
    post<{ ok: boolean }>(`/api/autopilot/${visitId}/enable`, data),

  disable: (visitId: string) =>
    post<{ ok: boolean }>(`/api/autopilot/${visitId}/disable`),

  getMode: (visitId: string) =>
    get<{ mode: string; active: boolean }>(`/api/autopilot/${visitId}/mode`),

  isActive: (visitId: string) =>
    get<{ active: boolean }>(`/api/autopilot/${visitId}/active`),

  updateConfig: (visitId: string, config: unknown) =>
    patch<{ ok: boolean }>(`/api/autopilot/${visitId}/config`, config),
};

// ─────────────────────────────────────────────────────────────────────────────
// 7. DOCUMENTS
// ─────────────────────────────────────────────────────────────────────────────

export interface DocumentUpload {
  clinicId: string;
  fileName: string;
  format: string;
  mimeType: string;
  sizeBytes: number;
  pageCount: number;
  storageUrl: string;
  category?: string;
  visitId?: string;
  tags?: string[];
}

export const documents = {
  upload: (data: DocumentUpload) =>
    post<{ id: string }>('/api/documents', data),

  get: (id: string) =>
    get<{ id: string; fileName: string; status: string }>(`/api/documents/${id}`),

  list: (params: { clinicId?: string; visitId?: string }) => {
    const qs = new URLSearchParams();
    if (params.clinicId) qs.set('clinicId', params.clinicId);
    if (params.visitId) qs.set('visitId', params.visitId);
    return get<unknown[]>(`/api/documents?${qs.toString()}`);
  },

  getStats: () =>
    get<{ totalDocuments: number; totalPages: number; totalSizeBytes: number; pendingOcr: number }>('/api/documents/stats'),

  triggerOcr: (id: string) =>
    post<{ ok: boolean }>(`/api/documents/${id}/ocr`),

  getOcrResult: (id: string) =>
    get<OcrJob>(`/api/documents/${id}/ocr`),

  update: (id: string, data: { visitId?: string; archive?: boolean }) =>
    patch<{ ok: boolean }>(`/api/documents/${id}`, data),
};

// ─────────────────────────────────────────────────────────────────────────────
// 8. QUOTA
// ─────────────────────────────────────────────────────────────────────────────

export const quota = {
  assignPlan: (data: { tenantId: string; clinicId: string; tier: string }) =>
    post<{ ok: boolean }>('/api/quota/tenants', data),

  listTenants: () =>
    get<unknown[]>('/api/quota/tenants'),

  getTenant: (id: string) =>
    get<{ tenantId: string; tier: string; usages: unknown[] }>(`/api/quota/tenants/${id}`),

  checkQuota: (data: { tenantId: string; dimension: string; amount: number }) =>
    post<{ allowed: boolean; remaining: number }>('/api/quota/check', data),

  recordUsage: (data: { tenantId: string; dimension: string; amount: number }) =>
    post<{ ok: boolean }>('/api/quota/record', data),

  getViolations: (tenantId?: string) => {
    const q = tenantId ? `?tenantId=${tenantId}` : '';
    return get<unknown[]>(`/api/quota/violations${q}`);
  },

  resetUsage: (id: string, dimension?: string) =>
    post<{ ok: boolean }>(`/api/quota/tenants/${id}/reset`, dimension ? { dimension } : undefined),

  getTierLimits: (tier: string) =>
    get<unknown>(`/api/quota/tiers/${tier}`),
};

// ─────────────────────────────────────────────────────────────────────────────
// 9. PROMPTS
// ─────────────────────────────────────────────────────────────────────────────

export const prompts = {
  list: () =>
    get<Array<{ id: string; kind: string; name: string; version: number; text: string; isDefault: boolean }>>('/api/prompts'),

  get: (promptId: string) =>
    get<{ id: string; kind: string; name: string; version: number; text: string; isDefault: boolean }>(`/api/prompts/${promptId}`),

  create: (data: { kind: string; name: string; text: string }) =>
    post<{ id: string }>('/api/prompts', data),

  update: (promptId: string, data: { name?: string; text?: string }) =>
    put<{ ok: boolean }>(`/api/prompts/${promptId}`, data),

  delete: (promptId: string) =>
    del<{ ok: boolean }>(`/api/prompts/${promptId}`),
};

// ─────────────────────────────────────────────────────────────────────────────
// 10. BINDINGS
// ─────────────────────────────────────────────────────────────────────────────

export const bindings = {
  list: () =>
    get<Array<{ id: string; slotName: string; workflowId: string; priority: number; active: boolean }>>('/api/bindings'),

  get: (bindingId: string) =>
    get<{ id: string; slotName: string; workflowId: string; priority: number; active: boolean; config: Record<string, unknown> }>(`/api/bindings/${bindingId}`),

  create: (data: { slotName: string; workflowId: string; priority?: number }) =>
    post<{ id: string }>('/api/bindings', data),

  update: (bindingId: string, data: { priority?: number; active?: boolean; config?: Record<string, unknown> }) =>
    put<{ ok: boolean }>(`/api/bindings/${bindingId}`, data),

  delete: (bindingId: string) =>
    del<{ ok: boolean }>(`/api/bindings/${bindingId}`),
};

// ─────────────────────────────────────────────────────────────────────────────
// 11. WORKFLOWS
// ─────────────────────────────────────────────────────────────────────────────

export const workflows = {
  list: () =>
    get<Array<{ id: string; name: string; version: number; nodeCount: number; updatedAt: number }>>('/api/workflows'),

  get: (workflowId: string) =>
    get<{ id: string; name: string; description: string; version: number; nodes: unknown[]; edges: unknown[]; updatedAt: number }>(`/api/workflows/${workflowId}`),

  create: (data: { name: string; description?: string; nodes: unknown[]; edges: unknown[] }) =>
    post<{ id: string }>('/api/workflows', data),

  update: (workflowId: string, data: { name?: string; description?: string; nodes?: unknown[]; edges?: unknown[] }) =>
    put<{ ok: boolean }>(`/api/workflows/${workflowId}`, data),

  delete: (workflowId: string) =>
    del<{ ok: boolean }>(`/api/workflows/${workflowId}`),
};

// ─────────────────────────────────────────────────────────────────────────────
// 12. LCVFR RUN CONSOLE ACTIONS
// ─────────────────────────────────────────────────────────────────────────────

export interface LcvfrRunActionResponse {
  ok: boolean;
  action: 'replay' | 'requeue';
  jobId: string;
  bindingId: string;
  workflowId: string;
  encounterId: string;
  mode: string;
}

export interface LcvfrRunTraceResponse {
  run: Record<string, unknown>;
  nodes: unknown[];
  artifacts: unknown[];
  exportedAt: string;
}

export const lcvfr = {
  execute: (data: { workflowId: string; encounterId?: string }) =>
    post<{ ok: boolean; jobId: string; workflowId: string; encounterId: string }>('/api/lcvfr/execute', data),

  replayRun: (runId: string, payload?: Record<string, unknown>) =>
    post<LcvfrRunActionResponse>(`/api/lcvfr/runs/${runId}/replay`, payload ? { payload } : undefined),

  requeueRun: (runId: string, payload?: Record<string, unknown>) =>
    post<LcvfrRunActionResponse>(`/api/lcvfr/runs/${runId}/requeue`, payload ? { payload } : undefined),

  exportTrace: (runId: string) =>
    get<LcvfrRunTraceResponse>(`/api/lcvfr/runs/${runId}/trace`),
};

// ─────────────────────────────────────────────────────────────────────────────
// 13. EVENT BUS
// ─────────────────────────────────────────────────────────────────────────────

export const events = {
  getMetrics: () =>
    get<{ published: number; delivered: number; errors: number }>('/api/events/metrics'),
};

// ─────────────────────────────────────────────────────────────────────────────
// 10. WEBSOCKET CLIENTS
// ─────────────────────────────────────────────────────────────────────────────

export const ws = {
  getStats: () =>
    get<{ connectedClients: number }>('/api/ws/stats'),

  getClient: (clientId: string) =>
    get<{ clientId: string; doctorId: string; role: string }>(`/api/ws/clients/${clientId}`),

  getLeader: (doctorId: string) =>
    get<{ clientId: string }>(`/api/ws/doctors/${doctorId}/leader`),

  getVisitClients: (visitId: string) =>
    get<unknown[]>(`/api/ws/visits/${visitId}/clients`),
};

// ─────────────────────────────────────────────────────────────────────────────
// 14. SETTINGS
// ─────────────────────────────────────────────────────────────────────────────

export const settingsApi = {
  get: (doctorId: string) => get<Record<string, unknown>>(`/api/settings/${doctorId}`),
  update: (doctorId: string, settings: Record<string, unknown>) =>
    put<{ ok: boolean }>(`/api/settings/${doctorId}`, settings),
};

// ─────────────────────────────────────────────────────────────────────────────
// Convenience re-export
// ─────────────────────────────────────────────────────────────────────────────

const api = {
  visits,
  jobs,
  consent,
  llm,
  smartfill,
  autopilot,
  documents,
  quota,
  prompts,
  bindings,
  workflows,
  lcvfr,
  events,
  ws,
  settingsApi,
  setAuthToken,
};

export default api;
